import React, { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import TextArea from 'components/Common/Form/Input/TextArea'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import * as Analytics from 'analytics/analytics'

interface Props {
  hotelCity: string
  freeText: string
  onFreeTextChange: (text: string) => void
  onGenerate: () => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripFreeTextSelection({
  hotelCity,
  freeText,
  onFreeTextChange,
  onGenerate,
  isGeneratingTrip,
  stepNumber,
  totalSteps,
}: Props) {
  const intl = useIntl()
  const handleFreeTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    onFreeTextChange(newValue)
  }, [onFreeTextChange])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'final_note',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const stepText = intl.formatMessage(
    {
      id: 'ewX/UMLdx4',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = intl.formatMessage(
    {
      id: 'l9RV45AnuD',
      defaultMessage: 'Final <i>note</i>',
      description: 'Heading text with italicized note',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const questionText = intl.formatMessage(
    {
      id: 'S9wf1Y9j8q',
      defaultMessage: 'Anything else we should know about your {hotelCity} trip?',
      description: 'Question asking for additional trip information',
    },
    { hotelCity },
  )

  const notesLabel = intl.formatMessage(
    {
      id: 'OdQBQEVNtm',
      defaultMessage: '<strong>Notes</strong> (optional)',
      description: 'Label for notes input field with optional indicator',
    },
    {
      strong: (chunks: React.ReactNode) => <strong key="bold">{chunks}</strong>,
    },
  )

  const placeholderText = intl.formatMessage({
    id: 'Nce2WLqL6t',
    defaultMessage: 'e.g. Need wheelchair access, Travelling with toddler, Bringing a service dog',
    description: 'Placeholder text for notes textarea with examples',
  })

  const createItineraryText = intl.formatMessage({
    id: 'inziQqsKZF',
    defaultMessage: 'Create my itinerary',
    description: 'Button text to create itinerary',
  })

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {questionText}
        </BodyText>
      </VerticalSpacer>

      <Group direction="vertical" gap={12} desktopGap={24}>
        <BodyText variant="large">
          {notesLabel}
        </BodyText>
        <TextArea
          value={freeText}
          onChange={handleFreeTextChange}
          placeholder={placeholderText}
          rows={3}
        />
      </Group>
      <div>
        <TextButton
          kind="primary"
          size="large"
          startIcon={<SolidSparklesIcon />}
          onClick={onGenerate}
          disabled={isGeneratingTrip}
        >
          {createItineraryText}
        </TextButton>
      </div>
    </Group>
  )
}

export default AITripFreeTextSelection
